import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Settings2, FolderUp, FileText, Mail } from "lucide-react";

export function FeePresetsDialog({ meta }) {
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/fleet/fee-presets").then(({ data }) => setPresets(data)).catch((e) => toast.error(formatApiError(e))); }, [open]);
  const set = (type, k) => (e) => setPresets((p) => ({ ...p, [type]: { ...(p[type] || {}), [k]: e.target.value } }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/fleet/fee-presets", presets); setPresets(data); toast.success("Fee presets saved — new drafts will use them"); setOpen(false); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="fee-presets-button"><Settings2 className="h-4 w-4 mr-1" />Fee presets</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Default fees per filing type</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">New drafts (including IFTA drafts generated from mileage) are pre-filled with these amounts. Choose <b>flat</b> per filing or <b>per unit</b> (× trucks / trailers included). Government fees are added when you file, once the agency amount is known.</p>
          <div className="space-y-3">
            {Object.entries(meta?.filing_types || {}).map(([k, label]) => {
              const perUnit = presets[k]?.pricing_mode === "per_unit";
              return (
              <div key={k} className="border rounded-md p-3 space-y-2" data-testid={`preset-row-${k}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold text-sm">{label}</div>
                  <div className="flex rounded-md border overflow-hidden text-xs">
                    <button type="button" onClick={() => setPresets((p) => ({ ...p, [k]: { ...(p[k] || {}), pricing_mode: "flat" } }))} className={`px-3 py-1 ${!perUnit ? "bg-blue-700 text-white" : "bg-white text-slate-600"}`} data-testid={`preset-mode-flat-${k}`}>Flat per filing</button>
                    <button type="button" onClick={() => setPresets((p) => ({ ...p, [k]: { ...(p[k] || {}), pricing_mode: "per_unit" } }))} className={`px-3 py-1 ${perUnit ? "bg-blue-700 text-white" : "bg-white text-slate-600"}`} data-testid={`preset-mode-unit-${k}`}>Per unit</button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 items-end">
                  {perUnit ? <>
                    <div><Label className="text-xs">Per truck ($)</Label><Input type="number" step="0.01" min={0} value={presets[k]?.per_truck_fee ?? ""} onChange={set(k, "per_truck_fee")} data-testid={`preset-truck-${k}`} /></div>
                    <div><Label className="text-xs">Per trailer ($)</Label><Input type="number" step="0.01" min={0} value={presets[k]?.per_trailer_fee ?? ""} onChange={set(k, "per_trailer_fee")} data-testid={`preset-trailer-${k}`} /></div>
                  </> : <div className="col-span-2"><Label className="text-xs">Service fee ($)</Label><Input type="number" step="0.01" min={0} value={presets[k]?.service_fee ?? ""} onChange={set(k, "service_fee")} data-testid={`preset-service-${k}`} /></div>}
                  <div><Label className="text-xs">Gov. fee ($) <span className="text-slate-400">default</span></Label><Input type="number" step="0.01" min={0} placeholder="add at filing" value={presets[k]?.gov_fee ?? ""} onChange={set(k, "gov_fee")} data-testid={`preset-gov-${k}`} /></div>
                </div>
              </div>
            ); })}
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="preset-save-button">{busy ? "Saving…" : "Save presets"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SendDigestButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Email every customer their 60-day fleet document outlook now?")) return;
    setBusy(true);
    try { const { data } = await api.post("/fleet/digest/run"); toast.success(`Fleet digest sent to ${data.sent} customer(s)`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return <Button variant="outline" onClick={run} disabled={busy} data-testid="send-fleet-digest-button"><Mail className="h-4 w-4 mr-1" />{busy ? "Sending…" : "Send fleet digest"}</Button>;
}

export function BulkDocsDialog({ customers, meta, defaultCustomer, onDone }) {
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState(defaultCustomer || "");
  const [docType, setDocType] = useState("2290 Schedule 1");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [assign, setAssign] = useState({});
  const [busy, setBusy] = useState(false);
  const reset = () => { setPreview(null); setAssign({}); setFile(null); };
  const upload = async () => {
    if (!file || !customerId) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("customer_id", customerId);
      const { data } = await api.post("/fleet/vehicles/documents/bulk-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); toast.success(`${data.matched} of ${data.entries.length} PDFs matched to units`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fleet/vehicles/documents/bulk-commit", { batch_id: preview.batch_id, doc_type: docType, expiry_date: expiry || null, assignments: assign });
      toast.success(`${data.attached} document(s) attached${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const resolved = preview ? preview.entries.filter((e) => e.vehicle_id || assign[e.id]).length : 0;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="bulk-docs-button"><FolderUp className="h-4 w-4 mr-1" />Upload documents (ZIP)</Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-docs-dialog">
          <DialogHeader><DialogTitle>Bulk upload vehicle documents</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Drop a ZIP of PDFs. Each file is matched to a unit by VIN or unit number in the filename, or by VIN found inside the PDF. Assign any leftovers manually.</p>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label className="text-xs">Company</Label><Select value={customerId} onValueChange={(v) => { setCustomerId(v); reset(); }}><SelectTrigger data-testid="bulk-docs-company"><SelectValue placeholder="Select company" /></SelectTrigger><SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Document type</Label><Select value={docType} onValueChange={setDocType}><SelectTrigger data-testid="bulk-docs-type"><SelectValue /></SelectTrigger><SelectContent>{(meta?.doc_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Expires (applies to all)</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="bulk-docs-expiry" /></div>
          </div>
          {!preview && (
            <div className="flex gap-2 items-end">
              <div className="flex-1"><Label className="text-xs">ZIP file</Label><Input type="file" accept=".zip,application/zip" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bulk-docs-file" /></div>
              <Button onClick={upload} disabled={!file || !customerId || busy} className="bg-blue-700 hover:bg-blue-800" data-testid="bulk-docs-upload">{busy ? "Matching…" : "Upload & match"}</Button>
            </div>
          )}
          {preview && (
            <>
              <div className="flex gap-2 text-sm"><Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="bulk-docs-matched">{preview.matched} matched</Badge>{preview.unmatched > 0 && <Badge className="bg-amber-50 text-amber-800 border border-amber-300" data-testid="bulk-docs-unmatched">{preview.unmatched} need a unit</Badge>}</div>
              <div className="border rounded-md divide-y max-h-80 overflow-auto">
                {preview.entries.map((e) => (
                  <div key={e.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`bulk-doc-entry-${e.id}`}>
                    <FileText className="h-4 w-4 text-blue-700 shrink-0" />
                    <div className="flex-1 min-w-0"><div className="font-mono text-xs truncate" title={e.file_name}>{e.file_name}</div>{e.matched_by && <div className="text-xs text-slate-500">{e.matched_by}</div>}</div>
                    {e.vehicle_id ? <Badge variant="outline" className="font-semibold">Unit {e.unit_number}</Badge> : (
                      <Select value={assign[e.id] || ""} onValueChange={(v) => setAssign((a) => ({ ...a, [e.id]: v }))}>
                        <SelectTrigger className="w-64 border-amber-300" data-testid={`bulk-doc-assign-${e.id}`}><SelectValue placeholder="Assign to unit…" /></SelectTrigger>
                        <SelectContent>{preview.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter className="justify-between">
                <Button variant="ghost" onClick={reset}>Start over</Button>
                <Button onClick={commit} disabled={busy || resolved === 0} className="bg-blue-700 hover:bg-blue-800" data-testid="bulk-docs-commit">{busy ? "Attaching…" : `Attach ${resolved} document${resolved === 1 ? "" : "s"}`}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
